/**
 * Redux Store Configuration
 * 
 * This file configures the Redux store for the multi-tenant revenue collection platform.
 * It combines all reducers, sets up middleware (thunk for async actions), and
 * initializes state from localStorage for persistence across sessions.
 * 
 * @module Store
 */

import { combineReducers, applyMiddleware, createStore } from 'redux';
import thunk from 'redux-thunk';
import { composeWithDevTools } from 'redux-devtools-extension';

// User reducers
import {
   userLoginReducer,
   userDetailsReducer,
   userTenantAccessReducer,
   userTenantListReducer,
   userTenantCreateReducer,
   userTenancyRegisterReducer,
   tenantDashboardDetailsReducer,
   userTenancyProfileReducer,
   userUpdateTenancyProfileReducer,
   tenantAnalyticsReducer as dashboardAnalyticsReducer,
    tenantLogoUploadReducer,
    userPermissionUpdateReducer,
    userSuspendReducer,
    userPasswordResetReducer,
    userPasswordChangeReducer,
} from './reducers/userReducers';

// Revenue reducers
import {
   revenueCreateReducer,
   revenueListReducer,
   revenueDeleteReducer,
   revenueUpdateReducer,
   revenueDetailsReducer
} from './reducers/revenueReducer';

// Demand category reducers
import {
   demandCategoryCreateReducer,
   demandCategoryUpdateReducer,
   demandCategoryDetailsReducer,
   demandCategoryDetailsRevenueReducer,
   categoryTemplatesReducer,
   categoryNamesForLgaReducer,
   categoryNameValidateReducer,
} from './reducers/demandCategoryReducer';

// Demand generation reducers
import {
   demandGenerateCreateReducer,
   demandGenerateDownloadReducer,
   demandGenerateBatchReducer,
   demandGenerateBatchNoticesReducer,
   demandGenerateListReducer
} from './reducers/demandGenerateReducer';

// Demand specific reducers
import {
   demandSpecificCreateReducer,
   demandSpecificDownloadReducer,
   demandSpecificBatchReducer,
   demandSpecificListReducer
} from './reducers/demandSpecificReducer';

// Demand edit reducers
import {
   demandSpecificEditReducer,
   demandBatchEditReducer
} from './reducers/demandEditReducers';

// Location reducers
import {
   locationListReducer,
   stateCreateReducer,
   stateListReducer,
   stateUpdateReducer,
   stateDetailsReducer,
   lgaCreateReducer,
   lgaListReducer,
   lgaUpdateReducer,
   lgaDetailsReducer
} from './reducers/locationReducer';

// Super Admin reducers - Platform-level management
import {
   platformAnalyticsReducer,
   tenantListReducer,
   tenantDetailsReducer,
   tenantUsersReducer,
   tenantAnalyticsReducer,
   tenantCreateReducer,
   tenantStatusUpdateReducer,
    auditLogsReducer,
    auditStatsReducer,
    platformUserListReducer,
    platformUserCreateReducer,
    platformUserUpdateReducer,
    platformUserStatusReducer,
    tenantCommercialTermsReducer,
    tenantCommercialTermsUpdateReducer,
    ownerReportConfigListReducer,
    ownerReportConfigDetailsReducer,
    ownerReportConfigCreateReducer,
    ownerReportConfigUpdateReducer,
    ownerReportConfigStatusReducer,
    ownerReportRunNowReducer,
    ownerReportRunsReducer,
    ownerReportRunDetailsReducer,
} from './reducers/superAdminReducers';

// Payer reducers
import { payerLookupReducer } from './reducers/payerReducer';

// Filter options reducer
import { filterOptionsReducer } from './reducers/filterOptionsReducer';

// Distribution reducers - Field agent distribution monitoring
import {
    distributionListReducer,
    distributionStatsReducer,
    flaggedDistributionsReducer,
     distributionReviewReducer,
     distributionBulkReviewReducer,
     agentLeaderboardReducer,
     distributionReportsReducer,
     distributionReportExportReducer,
     distributionReportRecordsReducer,
     distributionReportRecordsExportReducer,
     finalReminderCandidatesReducer,
     finalReminderExportReducer
 } from './reducers/distributionReducers';
import { paymentInformationListReducer } from './reducers/paymentInformationReducers';


const reducer = combineReducers({


   userLogin: userLoginReducer,
   userDetails: userDetailsReducer,
   userTenantAccess: userTenantAccessReducer,
   userTenancyRegister: userTenancyRegisterReducer,
   userTenancyProfile: userTenancyProfileReducer,
   tenantDashboardDetails: tenantDashboardDetailsReducer,
   userUpdateTenancyProfile: userUpdateTenancyProfileReducer,
   userTenantCreate: userTenantCreateReducer,
   userTenantList: userTenantListReducer,
   dashboardAnalytics: dashboardAnalyticsReducer,
   tenantLogoUpload: tenantLogoUploadReducer,
   userPermissionUpdate: userPermissionUpdateReducer,
   userSuspend: userSuspendReducer,
   userPasswordReset: userPasswordResetReducer,
   userPasswordChange: userPasswordChangeReducer,


   demandCategoryCreate: demandCategoryCreateReducer,
   demandCategoryUpdate: demandCategoryUpdateReducer,
   demandCategoryDetails: demandCategoryDetailsReducer,
   demandCategoryDetailsRevenue: demandCategoryDetailsRevenueReducer,
   categoryTemplates: categoryTemplatesReducer,
   categoryNamesForLga: categoryNamesForLgaReducer,
   categoryNameValidate: categoryNameValidateReducer,


   demandGenerateCreate: demandGenerateCreateReducer,
   demandGenerateDownload: demandGenerateDownloadReducer,
   demandGenerateBatch: demandGenerateBatchReducer,
   demandGenerateBatchNotices: demandGenerateBatchNoticesReducer,
   demandGenerateList: demandGenerateListReducer,


   demandSpecificCreate: demandSpecificCreateReducer,
   demandSpecificDownload: demandSpecificDownloadReducer,
   demandSpecificBatch: demandSpecificBatchReducer,
   demandSpecificList: demandSpecificListReducer,

   locationList: locationListReducer,

   stateCreate: stateCreateReducer,
   stateList: stateListReducer,
   stateUpdate: stateUpdateReducer,
   stateDetails: stateDetailsReducer,


   lgaCreate: lgaCreateReducer,
   lgaList: lgaListReducer,
   lgaUpdate: lgaUpdateReducer,
   lgaDetails: lgaDetailsReducer,


   revenueCreate: revenueCreateReducer,
   revenueList: revenueListReducer,
   revenueDelete: revenueDeleteReducer,
   revenueUpdate: revenueUpdateReducer,
   revenueDetails: revenueDetailsReducer,

   // Super Admin reducers
   platformAnalytics: platformAnalyticsReducer,
   tenantList: tenantListReducer,
   tenantDetails: tenantDetailsReducer,
   tenantUsers: tenantUsersReducer,
   tenantAnalytics: tenantAnalyticsReducer,
   tenantCreate: tenantCreateReducer,
   tenantStatusUpdate: tenantStatusUpdateReducer,
    auditLogs: auditLogsReducer,
    auditStats: auditStatsReducer,
    platformUserList: platformUserListReducer,
    platformUserCreate: platformUserCreateReducer,
    platformUserUpdate: platformUserUpdateReducer,
    platformUserStatus: platformUserStatusReducer,
    tenantCommercialTerms: tenantCommercialTermsReducer,
   tenantCommercialTermsUpdate: tenantCommercialTermsUpdateReducer,
   ownerReportConfigList: ownerReportConfigListReducer,
   ownerReportConfigDetails: ownerReportConfigDetailsReducer,
   ownerReportConfigCreate: ownerReportConfigCreateReducer,
   ownerReportConfigUpdate: ownerReportConfigUpdateReducer,
   ownerReportConfigStatus: ownerReportConfigStatusReducer,
   ownerReportRunNow: ownerReportRunNowReducer,
   ownerReportRuns: ownerReportRunsReducer,
   ownerReportRunDetails: ownerReportRunDetailsReducer,

   // Payer reducers
   payerLookup: payerLookupReducer,

   // Filter options (LGAs and Categories for search)
   filterOptions: filterOptionsReducer,

    // Demand edit reducers
    demandSpecificEdit: demandSpecificEditReducer,
    demandBatchEdit: demandBatchEditReducer,

    // Distribution monitoring reducers
    distributionList: distributionListReducer,
     distributionStats: distributionStatsReducer,
     flaggedDistributions: flaggedDistributionsReducer,
     distributionReview: distributionReviewReducer,
     distributionBulkReview: distributionBulkReviewReducer,
     agentLeaderboard: agentLeaderboardReducer,
     distributionReports: distributionReportsReducer,
     distributionReportExport: distributionReportExportReducer,
     distributionReportRecords: distributionReportRecordsReducer,
     distributionReportRecordsExport: distributionReportRecordsExportReducer,
     finalReminderCandidates: finalReminderCandidatesReducer,
     finalReminderExport: finalReminderExportReducer,
     paymentInformationList: paymentInformationListReducer
  });

const userInfoFromStorage = localStorage.getItem('userInfo')
   ? JSON.parse(localStorage.getItem('userInfo'))
   : null;
const userInfoTenancyFromStorage = localStorage.getItem('userInfoTenancy')
   ? JSON.parse(localStorage.getItem('userInfoTenancy'))
   : null;
const tenantFromStorage = localStorage.getItem('tenant')
   ? JSON.parse(localStorage.getItem('tenant'))
   : null;
const demandSpecificListFromStorage = localStorage.getItem('demand_Specificlists')
   ? JSON.parse(localStorage.getItem('demand_Specificlists'))
   : null;
const demandGenerateListFromStorage = localStorage.getItem('demand_lists')
   ? JSON.parse(localStorage.getItem('demand_lists'))
   : null;

const initialState = {
   userLogin: { userInfo: userInfoFromStorage },
   userTenancyProfile: { userInfoTenancy: userInfoTenancyFromStorage },
   tenantDashboardDetails: { tenant: tenantFromStorage },
   demandSpecificList: { demand_Specificlists: demandSpecificListFromStorage },
   demandGenerateList: { demand_lists: demandGenerateListFromStorage },
};

const middleware = [thunk];

const store = createStore(
   reducer,
   initialState,
   composeWithDevTools(applyMiddleware(...middleware))
);

export default store;
